import { itemsAtom, optionGroupsAtom } from "@/atoms"
import { prizeOfferOf, type PrizeCatalog, type PrizeChoice, type PrizeOffer } from "@/domain/marketing/prize-offer"
import { useBrowsingChannelCatalog } from "@/hooks/use-browsing-channel-catalog"
import { useAddFreeItem } from "@/hooks/use-add-free-item"
import { getMerchantId } from "@/lib/company-storage"
import type { CouponBenefit } from "@/lib/coupon-benefit"
import { getPendingCouponCodes, keepCouponForLater, savePendingCouponCodes } from "@/lib/pending-coupon"
import { useAtomValue } from "jotai"
import { useNavigate } from "react-router-dom"

/** What the customer chose to do with a prize: attach it to the order, browse the menu, or take one product. */
export type PrizeRedemption = { kind: "use" } | { kind: "menu" } | { kind: "item"; choice: PrizeChoice }

/** Puts a prize code on this visit's order (and keeps it across visits), so cart and checkout apply it without typing. */
export function attachPrizeCode(code: string): void {
  const merchantId = getMerchantId()
  if (!merchantId) return
  savePendingCouponCodes([...getPendingCouponCodes(merchantId), code], merchantId)
  keepCouponForLater(code, merchantId)
}

/** The catalog a prize resolves against: what the storefront menu offers right now. */
export function usePrizeCatalog(): PrizeCatalog {
  const items = useAtomValue(itemsAtom)
  const optionGroups = useAtomValue(optionGroupsAtom)
  const { visibleItemOffers, visibleCategories } = useBrowsingChannelCatalog()
  return { items, optionGroups, visibleItemOffers, visibleCategories }
}

/** How to spend a prize with today's menu, or null until its benefit is known. */
export function usePrizeOffer(benefit: CouponBenefit | null): PrizeOffer | null {
  const catalog = usePrizeCatalog()
  return benefit ? prizeOfferOf(benefit, catalog) : null
}

/**
 * Spends a prize: attaches its code, then goes where the order continues — the
 * bag, with the chosen product already in it when it needs no choices; the
 * product sheet when it does; the menu for a prize to browse for. The price is
 * never set here: the bag line keeps its menu price and the coupon evaluation
 * takes it off. `onLeave` runs before navigating (closes the game sheet).
 */
export function useRedeemPrize(onLeave?: () => void): (code: string, redemption: PrizeRedemption) => void {
  const navigate = useNavigate()
  const addFreeItem = useAddFreeItem()

  function addChoice(choice: PrizeChoice): string {
    const addition = addFreeItem(choice)
    return addition.added ? "/cart" : addition.to
  }

  return (code, redemption) => {
    attachPrizeCode(code)
    const to = redemption.kind === "item" ? addChoice(redemption.choice) : redemption.kind === "menu" ? "/" : "/cart"
    onLeave?.()
    navigate(to)
  }
}
